import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  DataGridPro,
  UncapitalizedGridProSlotsComponent,
} from '@mui/x-data-grid-pro';
import {Box, Typography} from '@mui/material';
import {GridRenderCellParams} from '@mui/x-data-grid';
import {IMediaTrack, ITag} from '../../dto/MediaTrack.dto';
import {
  DataGridCustPagination,
  RequestSongGridFooter,
} from '../utlis/DataGrid/Pagination';
import MusicPreviewBtn, {MusicPreviewBtnDisable} from './MusicPreviewBtn';
import {defaultDataGridProSearchSort} from '../../dto/Html.dto';
import {useStoreSelector} from '../../store/configstore';
import {companyIdSelector} from '../../store/Slices/companySlice';
import {
  useRqMediaTracksPaginate,
  useRqMediaTracksWithoutCompanyPaginate,
} from '../../react_query/media_tracks/ListMediaTrackPaginatPro';
import {NoPlayInfoButton} from '../../pages/admin/no-play/NoPlayInfoButton';
import {SongSelectionRemoveButton} from '../Button/SongSelectionRemoveButton';
import {SongSelectionAddButton} from '../Button/SongSelectionAddButton';
import {GridDurationColumn} from '../grid/GridColumns';
import {IStation} from '../../dto/Station.dto';
import FuzzySearchSong from '../../pages/music/search/FuzzySearchSong';
import CustomNoRowsOverlay from '../utlis/DataGrid/CustomNoRowsOverlay';
import IconRequestSong from '../../asset/icons/icon_addsong.svg';
import {messageConfig} from '../../constants/messages';
import DialogConfirmRequestSong from '../dialogs/DialogConfirmRequestSong';
import {iTimedMessageMessageItem} from '../../pages/messages/timed-message/request/contents/TimedMsgRequestContentMessageCard';
import {ConditionalRender} from '../UserCan';
import AdminFilterOnly from '../../pages/music/search/AdminFilterOnly';

type Props = {
  selectedTracks: IMediaTrack[];
  onSelect: (track: IMediaTrack) => void;
  onDeselect: (track: IMediaTrack) => void;
  isAddDisabled?: boolean;
  company_id?: number;
  slots?: Partial<UncapitalizedGridProSlotsComponent>;
  title?: string;
  selectedStations?: IStation[];
  type?: string;
  musicType?: string;
  isTrackWithoutGenreRestriction?: boolean;
  selection_type?: string;
  onChange?: (newMessage: iTimedMessageMessageItem) => void;
  message?: iTimedMessageMessageItem;
  containerHeight?: string | number;
  containerWidth?: string | number;
  showTitle?: boolean;
  showAdminFilter?: boolean;
  station_id?: number;
  onSelectAll?: (tracks: IMediaTrack[]) => void;
};

const MusicSelectionTable = ({
  selectedTracks,
  onSelect,
  onDeselect,
  onChange,
  message,
  isAddDisabled,
  company_id,
  musicType,
  slots = {pagination: DataGridCustPagination},
  title,
  isTrackWithoutGenreRestriction,
  selectedStations,
  type,
  selection_type,
  containerHeight = '480px',
  containerWidth = '99%',
  showTitle = true,
  showAdminFilter = false,
  onSelectAll,
}: Props) => {
  const [selectedTrackIds, setSelectedTrackIds] = useState(() =>
    selectedTracks.map(o => o.id),
  );

  const [isOpenRequestSong, setIsOpenRequestSong] = useState(false);
  const [tag, setTag] = useState<ITag | null>(null);

  const [query, setQuery] = useState({
    ...defaultDataGridProSearchSort,
    ...(selectedStations && {station_id: selectedStations.map(val => val.id)}),
    ...(type && {type: type}),
    ...(selection_type && {
      selection_type: selection_type,
      track_artist: '',
      track_name: '',
      fuzzy: false,
    }),
    mediaTags: [] as number[],
  });

  const companySelector = useStoreSelector(companyIdSelector);
  const companyId = company_id ?? companySelector;

  const {data, isLoading} = useRqMediaTracksWithoutCompanyPaginate(
    query,
    isTrackWithoutGenreRestriction,
  );
  const {
    data: mediaTrackData,
    isLoading: mediaTrackIsLoading,
    isFetching,
  } = useRqMediaTracksPaginate(
    query,
    companyId,
    !isTrackWithoutGenreRestriction,
  );

  const enableSongRequest = useMemo(() => {
    return (
      selection_type === 'timed-message' &&
      query.fuzzy &&
      query.track_artist !== '' &&
      query.track_name !== ''
    );
  }, [selection_type, query]);

  const trackData = useMemo(() => {
    if (!mediaTrackIsLoading || !isLoading) {
      return mediaTrackData?.data || data?.data || [];
    }
    return [];
  }, [data, mediaTrackData, mediaTrackIsLoading, isLoading]);

  const isSame = useMemo(() => {
    return trackData.some(t => t.is_same === true);
  }, [trackData]);

  const hasOnlyNoplaylistTracks = useMemo(() => {
    return trackData.every(t => t.track_category === 5);
  }, [trackData]);

  const shouldShowRequestRow = useMemo(() => {
    return enableSongRequest && hasOnlyNoplaylistTracks;
  }, [enableSongRequest, hasOnlyNoplaylistTracks]);

  const allSelected = useMemo(() => {
    if (trackData.length === 0) return false;

    const selectedIds = new Set(selectedTracks.map(song => song.id));
    return trackData.every(mt => selectedIds.has(mt.id));
  }, [trackData, selectedTracks]);

  const updateQuery = useCallback(
    updates => {
      setQuery(prev => ({...prev, ...updates}));
    },
    [setQuery],
  );

  const handleCloseRequestSong = useCallback(() => {
    setIsOpenRequestSong(false);
  }, []);

  const handleOpenRequestSong = useCallback(() => {
    setIsOpenRequestSong(true);
  }, []);

  useEffect(() => {
    setSelectedTrackIds(selectedTracks.map(o => o.id));
    if (musicType) updateQuery({type: musicType});
  }, [selectedTracks, musicType, updateQuery]);

  const columns = useMemo(
    () =>
      [
        {
          field: 'track_artist',
          headerName: 'Artist',
          flex: 1,
        },
        {
          field: 'track_name',
          headerName: 'Song Title',
          flex: 1,
        },
        GridDurationColumn,
        {
          field: 'id',
          type: 'actions',
          headerName: 'Play',
          width: 54,
          flex: 1,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            params.row.track_category === 5 ? (
              <MusicPreviewBtnDisable isNoPlay={true} />
            ) : params.row.url ? (
              <MusicPreviewBtn {...params.row} />
            ) : null,
        },
        {
          type: 'actions',
          field: 'actions',
          headerName: 'Action',
          hideable: false,
          width: 125,
          flex: 1,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            params.row.track_category === 5 ? (
              <NoPlayInfoButton id={params.row.id} />
            ) : selectedTrackIds.includes(params.row.id) ? (
              <SongSelectionRemoveButton
                onClick={() => onDeselect(params.row)}
              />
            ) : (
              <SongSelectionAddButton
                onClick={() => onSelect(params.row)}
                disabled={isAddDisabled}
              />
            ),
        },
      ].map(column => ({...column, sortable: false, filterable: false})),
    [selectedTrackIds, onSelect, onDeselect, isAddDisabled],
  );

  const rowCount = useMemo(() => {
    if (isTrackWithoutGenreRestriction) {
      return data?.total || 0;
    }
    return mediaTrackData?.total || 0;
  }, [data?.total, isTrackWithoutGenreRestriction, mediaTrackData?.total]);

  const handleFuzzyChange = useCallback(
    (filters: {track_artist: string; track_name: string; fuzzy: boolean}) => {
      setQuery(prev => ({
        ...prev,
        ...filters,
        page: 0,
      }));
    },
    [setQuery],
  );

  const handleFuzzyReset = useCallback(() => {
    setQuery(prev => ({
      ...prev,
      track_artist: '',
      track_name: '',
      fuzzy: false,
      page: 0,
    }));
  }, [setQuery]);

  const handleAddSongRequest = useCallback(() => {
    if (onChange && message) {
      onChange({
        ...message,
        artist: query.track_artist ?? '',
        title: query.track_name ?? '',
        file: undefined,
        track: undefined,
        isNew: true,
      });
    }
    handleFuzzyReset();
    handleCloseRequestSong();
  }, [
    handleCloseRequestSong,
    handleFuzzyReset,
    onChange,
    message,
    query.track_artist,
    query.track_name,
  ]);

  useEffect(() => {
    setQuery(prev => ({...prev, page: 0}));
  }, [musicType]);

  useEffect(() => {
    setQuery(prev => ({
      ...prev,
      mediaTags: tag?.id ? [tag.id] : [],
    }));
  }, [tag]);

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      if (isChecked && onSelectAll) {
        onSelectAll(trackData);
      } else if (!isChecked) {
        // Deselect only current page tracks
        trackData.forEach(track => {
          onDeselect(track);
        });
      }
    },
    [trackData, onSelectAll, onDeselect],
  );

  return (
    <Box className='w-full h-full flex-column justify-start'>
      <Box className='w-full h-62 flex flex-row justify-between items-center'>
        <ConditionalRender condition={showTitle}>
          <Typography
            variant='h5'
            aria-level={2}
            className='text-white font-bold'>
            {title || 'Select Songs'}
          </Typography>
        </ConditionalRender>
        <FuzzySearchSong
          onReset={handleFuzzyReset}
          onFuzzySearchChange={handleFuzzyChange}
          trackArtist={query.track_artist ?? ''}
          trackName={query.track_name ?? ''}
        />
        {showAdminFilter && (
          <AdminFilterOnly
            allSelected={allSelected}
            handleSelectAll={handleSelectAll}
            tag={tag}
            setTag={setTag}
            type={type}
          />
        )}
      </Box>
      <Box
        className='scroll-y'
        sx={{
          height: containerHeight,
          maxHeight: containerHeight,
          width: containerWidth,
        }}>
        <DataGridPro
          experimentalFeatures={{ariaV7: true}}
          className='dataGrid-header-visible'
          rows={trackData}
          columns={columns}
          loading={
            isTrackWithoutGenreRestriction ? isLoading : mediaTrackIsLoading
          }
          rowCount={rowCount}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{page: query.page, pageSize: query.pageSize}}
          paginationMode='server'
          pagination
          onPaginationModelChange={updateQuery}
          onSortModelChange={model => updateQuery({sort: model})}
          density='compact'
          disableRowSelectionOnClick
          disableColumnMenu
          slots={{
            ...slots,
            footer:
              enableSongRequest && !isSame && trackData.length > 0
                ? () => (
                    <RequestSongGridFooter
                      enableSongRequest={true}
                      onRequestSongClick={handleOpenRequestSong}
                    />
                  )
                : undefined,
            noRowsOverlay:
              shouldShowRequestRow && !isFetching
                ? () => (
                    <CustomNoRowsOverlay
                      imageSrc={IconRequestSong}
                      message={messageConfig.requestSong.message}
                      buttonText={messageConfig.requestSong.buttonText}
                      IconButton={IconRequestSong}
                      onButtonClick={handleOpenRequestSong}
                      onClearClick={handleFuzzyReset}
                    />
                  )
                : undefined,
          }}
          slotProps={{
            columnsPanel: {
              disableHideAllButton: true,
              disableShowAllButton: true,
            },
          }}
        />
        {isOpenRequestSong && (
          <DialogConfirmRequestSong
            track_name={query.track_name ?? ''}
            track_artist={query.track_artist ?? ''}
            onCancel={handleCloseRequestSong}
            handleOk={handleAddSongRequest}
          />
        )}
      </Box>
    </Box>
  );
};

export default MusicSelectionTable;
